/**
 * RelayStatusTracker Component
 *
 * Shows live relay/swap status with progress indication.
 * Polls the relay status endpoint and displays steps.
 */

import React, { useEffect, useState, useRef } from 'react';
import {
    Box,
    Typography,
    LinearProgress,
    Alert,
    Link,
    Chip,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';

const STATUS_STEPS = {
    pending: { label: 'Pending', progress: 10, color: '#ff9800' },
    submitted: { label: 'Transaction Submitted', progress: 50, color: '#2196f3' },
    confirmed: { label: 'Confirmed', progress: 100, color: '#4caf50' },
    failed: { label: 'Failed', progress: 100, color: '#f44336' },
    delegated: { label: 'Delegated to another node', progress: 20, color: '#9c27b0' },
};

function RelayStatusTracker({ jobId, nodeUrl, onComplete, blockExplorer }) {
    const [status, setStatus] = useState('pending');
    const [txHash, setTxHash] = useState(null);
    const [gasUsed, setGasUsed] = useState(null);
    const [error, setError] = useState(null);
    const intervalRef = useRef(null);

    useEffect(() => {
        if (!jobId || !nodeUrl) return;

        const poll = async () => {
            try {
                const response = await fetch(`${nodeUrl}/api/v2/relay/status/${jobId}`);
                const data = await response.json();

                if (data.success && data.job) {
                    const job = data.job;
                    setStatus(job.status);

                    if (job.tx_hash) setTxHash(job.tx_hash);
                    if (job.gas_used) setGasUsed(job.gas_used);
                    if (job.error) setError(job.error);

                    if (job.status === 'confirmed' || job.status === 'failed') {
                        clearInterval(intervalRef.current);
                        onComplete?.(job);
                    }
                }
            } catch (err) {
                console.warn('Status poll error:', err.message);
            }
        };

        poll();
        intervalRef.current = setInterval(poll, 3000);

        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
    }, [jobId, nodeUrl, onComplete]);

    const step = STATUS_STEPS[status] || STATUS_STEPS.pending;
    const isComplete = status === 'confirmed';
    const isFailed = status === 'failed';

    return (
        <Box sx={{
            p: 2,
            backgroundColor: 'rgba(255,255,255,0.03)',
            borderRadius: 1,
            border: `1px solid ${step.color}33`,
        }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                {isComplete ? (
                    <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 20 }} />
                ) : isFailed ? (
                    <ErrorIcon sx={{ color: '#f44336', fontSize: 20 }} />
                ) : (
                    <HourglassEmptyIcon sx={{ color: '#ff9800', fontSize: 20 }} />
                )}
                <Typography variant="body2" sx={{ fontWeight: 600, color: step.color }}>
                    {step.label}
                </Typography>
                <Chip
                    label="Gas-Free"
                    size="small"
                    sx={{
                        ml: 'auto',
                        height: 20,
                        fontSize: '0.65rem',
                        backgroundColor: 'rgba(76,175,80,0.15)',
                        color: '#4caf50',
                    }}
                />
            </Box>

            <LinearProgress
                variant="determinate"
                value={step.progress}
                sx={{
                    mb: 1.5,
                    height: 4,
                    borderRadius: 2,
                    backgroundColor: 'rgba(255,255,255,0.05)',
                    '& .MuiLinearProgress-bar': {
                        backgroundColor: step.color,
                    },
                }}
            />

            {txHash && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="caption" sx={{ color: '#888' }}>
                        Transaction
                    </Typography>
                    {blockExplorer ? (
                        <Link
                            href={`${blockExplorer}/tx/${txHash}`}
                            target="_blank"
                            rel="noopener"
                            variant="caption"
                            sx={{ color: '#90caf9' }}
                        >
                            {txHash.slice(0, 10)}...{txHash.slice(-8)}
                        </Link>
                    ) : (
                        <Typography variant="caption" sx={{ color: '#aaa', fontFamily: 'monospace' }}>
                            {txHash.slice(0, 10)}...{txHash.slice(-8)}
                        </Typography>
                    )}
                </Box>
            )}

            {gasUsed > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="caption" sx={{ color: '#888' }}>
                        Gas used
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#aaa' }}>
                        {gasUsed.toLocaleString()}
                    </Typography>
                </Box>
            )}

            {isFailed && error && (
                <Alert severity="error" sx={{ mt: 1, fontSize: '0.75rem' }}>
                    {error}
                </Alert>
            )}

            {!isComplete && !isFailed && (
                <Typography variant="caption" sx={{ color: '#666', display: 'block', mt: 1 }}>
                    Relayer is processing your withdrawal. You don't need to pay gas.
                </Typography>
            )}
        </Box>
    );
}

export default RelayStatusTracker;
